import React, { useCallback, useContext, useEffect, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { format, parseJSON } from 'date-fns'
import api from '../../../lib/api'
import { QuestionnaireInviteReportType, QuestionnaireInviteReportRowType } from '../../../lib/types'
import { SimpleSnackbarContext } from '../../../components/SimpleSnackbar'

interface InviteReportModalProps {
  questionnaire: { id: string; title: string } | null
  onClose: () => void
}

const completionChip = (row: QuestionnaireInviteReportRowType) => {
  if (row.finished) {
    return <Chip size="small" color="success" label="Finished" />
  }
  if (row.completed) {
    return <Chip size="small" color="success" variant="outlined" label="All answered" />
  }
  if (row.started) {
    return <Chip size="small" color="warning" variant="outlined" label="In progress" />
  }
  return <Chip size="small" variant="outlined" label="Not started" />
}

const formatDate = (value?: string) => {
  if (!value) return '-'
  try {
    return format(parseJSON(value), 'd MMM yyyy HH:mm')
  } catch {
    return '-'
  }
}

const InviteReportModal: React.FC<InviteReportModalProps> = ({ questionnaire, onClose }) => {
  const { actions: snackActions } = useContext(SimpleSnackbarContext)
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<QuestionnaireInviteReportType | null>(null)

  useEffect(() => {
    if (!questionnaire) {
      setReport(null)
      return
    }
    let cancelled = false
    const fetchReport = async () => {
      setLoading(true)
      try {
        const response = await api.getQuestionnaireInviteReport(questionnaire.id)
        if (!cancelled) setReport(response)
      } catch {
        if (!cancelled) {
          snackActions.open('Ops! Something went wrong loading the report, please try again later.')
          onClose()
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchReport()
    return () => {
      cancelled = true
    }
  }, [questionnaire])

  const handleDownloadCsv = useCallback(async () => {
    if (!questionnaire) return
    try {
      await api.downloadQuestionnaireInviteLinks(questionnaire.id)
      snackActions.open('Your download should start soon!')
    } catch {
      snackActions.open('Ops! Something went wrong trying to download the CSV, please try again later.')
    }
  }, [questionnaire, snackActions])

  return (
    <Dialog open={!!questionnaire} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Upload report for: {questionnaire?.title}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && report && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginBottom: 2 }}>
              <Typography fontWeight="700" fontSize="0.95em">
                Invitees: {report.inviteCount}
              </Typography>
              <Typography fontWeight="700" fontSize="0.95em">
                Started: {report.startedCount}
              </Typography>
              <Typography fontWeight="700" fontSize="0.95em">
                Finished: {report.finishedCount}
              </Typography>
              <Typography fontWeight="700" fontSize="0.95em">
                Videos submitted: {report.totalSubmitted}
              </Typography>
              <Typography fontWeight="700" fontSize="0.95em" sx={{ color: 'success.main' }}>
                Uploads succeeded: {report.totalUploadsSucceeded}
              </Typography>
              <Typography fontWeight="700" fontSize="0.95em" sx={{ color: 'error.main' }}>
                Uploads failed: {report.totalUploadsFailed}
              </Typography>
            </Box>
            {report.invites.length === 0 ? (
              <Typography sx={{ padding: 2 }}>No invitees found for this questionnaire.</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invitee</TableCell>
                      <TableCell align="center">Consented</TableCell>
                      <TableCell align="center">Started</TableCell>
                      <TableCell align="center">Answered</TableCell>
                      <TableCell align="center">Skipped</TableCell>
                      <TableCell align="center">Uploads OK</TableCell>
                      <TableCell align="center">Uploads failed</TableCell>
                      <TableCell align="center">Completion</TableCell>
                      <TableCell align="center">Last activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.invites.map((row, idx) => (
                      <TableRow key={`${row.email}-${idx}`} hover>
                        <TableCell>
                          <Typography fontSize="0.9em" fontWeight={600}>
                            {row.name || '-'}
                          </Typography>
                          <Typography fontSize="0.8em" color="text.secondary">
                            {row.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">{row.consentedAt ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="center">{row.started ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="center">
                          {row.submitted}/{report.totalQuestions}
                        </TableCell>
                        <TableCell align="center">{row.skipped}</TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: row.uploadsSucceeded > 0 ? 'success.main' : 'text.secondary'
                            }}
                          >
                            {row.uploadsSucceeded > 0 && <CheckCircleIcon fontSize="small" />}
                            {row.uploadsSucceeded}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              color: row.uploadsFailed > 0 ? 'error.main' : 'text.secondary'
                            }}
                          >
                            {row.uploadsFailed > 0 && <ErrorIcon fontSize="small" />}
                            {row.uploadsFailed}
                          </Box>
                        </TableCell>
                        <TableCell align="center">{completionChip(row)}</TableCell>
                        <TableCell align="center">
                          <Typography fontSize="0.8em">{formatDate(row.lastActivityAt)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={handleDownloadCsv} disabled={loading}>
          Download CSV
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default InviteReportModal
